import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sun, Moon, ChevronDown, User as UserIcon, Settings as SettingsIcon, LogOut } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import photoUrl from '../../../utils/photoUrl';
import { toClassicHref } from './adminUiRoutes';
import { AdminWorkspace } from './AdminWorkspace';
import { useAdminModules } from './adminModules';
import '../../../styles/admin-v2.css';

/* ============================================================
   Guscio condiviso area admin V2 (redesign 2026).
   Fornisce: wrapper .adm2, barra superiore (logo + chip utente +
   link "versione classica"), breadcrumb, intestazione pagina e
   toggle tema. Ogni modulo V2 lo usa per non duplicare il layout.
   ============================================================ */

export const V2ThemeToggle: React.FC<{ onBand?: boolean }> = ({ onBand }) => {
  const { theme, toggleTheme } = useTheme();
  return (
    <button
      className={`a2-btn ${onBand ? 'a2-btn--ghost-band' : 'a2-btn--ghost'}`}
      onClick={toggleTheme}
      title="Tema chiaro / scuro"
    >
      {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
    </button>
  );
};

interface Crumb { label: string; to?: string; }

interface V2ShellProps {
  /** Indirizzo ufficiale della pagina corrente: il link "Versione classica" passa
   *  dall'interruttore /admin/classica e riapre lo stesso indirizzo in modalità classica. */
  classicHref?: string;
  classicLabel?: string;
  /** In alternativa a classicHref: bottone che esce dalla modalità V2 restando sulla pagina */
  onExitV2?: () => void;
  /** Briciole: l'ultima è la pagina corrente (senza link) */
  breadcrumb?: Crumb[];
  children: React.ReactNode;
}

/* Chip utente con menu a tendina (come il Header classico: foto/iniziali,
   nome completo, Il mio profilo / Impostazioni / Logout) */
export const V2UserMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const displayName = user?.profile?.fullName || user?.username || 'Amministratore';
  const initials = displayName.trim().split(/\s+/).slice(0, 2).map(p => p[0]).join('').toUpperCase() || 'A';
  // `foto` e` la foto caricata da /profile (percorso relativo -> URL assoluto);
  // `profilePicture` resta come fallback per i profili storici.
  const photo = photoUrl(user?.profile?.foto) || user?.profile?.profilePicture;

  const itemStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left',
    padding: '9px 14px', font: "500 12.5px 'DM Sans', sans-serif", color: 'var(--a2-tx)',
    background: 'transparent', border: 'none', cursor: 'pointer', textDecoration: 'none',
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, font: "500 13px 'DM Sans', sans-serif", color: 'var(--a2-mut)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 999 }}
        title="Menu utente"
      >
        {photo && !imgError ? (
          <img src={photo} alt={displayName} onError={() => setImgError(true)}
            style={{ width: 28, height: 28, borderRadius: 999, objectFit: 'cover', flex: 'none' }} />
        ) : (
          <span style={{ width: 28, height: 28, borderRadius: 999, background: 'var(--a2-green-800)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', font: "700 11px 'Sora', sans-serif", flex: 'none' }}>
            {initials}
          </span>
        )}
        <span style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</span>
        <ChevronDown size={14} style={{ flex: 'none', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
      </button>

      {open && (
        <div className="a2-card" style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', width: 200, padding: '6px 0', zIndex: 120, boxShadow: '0 12px 30px rgba(10,77,44,.15)' }}>
          <Link to="/profile" style={itemStyle} onClick={() => setOpen(false)}>
            <UserIcon size={14} style={{ color: 'var(--a2-mut)' }} /> Il mio profilo
          </Link>
          <Link to="/admin/settings" style={itemStyle} onClick={() => setOpen(false)}>
            <SettingsIcon size={14} style={{ color: 'var(--a2-mut)' }} /> Impostazioni
          </Link>
          <div style={{ borderTop: '1px solid var(--a2-bd2)', margin: '6px 0' }} />
          <button style={{ ...itemStyle, color: 'var(--a2-dang-fg)' }}
            onClick={async () => { setOpen(false); try { await logout(); } catch (e) { console.error('Logout error:', e); } }}>
            <LogOut size={14} /> Logout
          </button>
        </div>
      )}
    </div>
  );
};

export const V2Shell: React.FC<V2ShellProps> = ({ classicHref, classicLabel = 'Versione classica', onExitV2, breadcrumb, children }) => {
  const { modules } = useAdminModules();
  const crumbs = breadcrumb && breadcrumb.length > 0 ? (
    <span className="dash-breadcrumb" aria-label="Percorso">
      {breadcrumb.map((c, i) => {
        const last = i === breadcrumb.length - 1;
        return (
          <React.Fragment key={i}>
            {c.to && !last ? <Link to={c.to}>{c.label}</Link> : last ? <strong>{c.label}</strong> : c.label}
            {!last && <span>/</span>}
          </React.Fragment>
        );
      })}
    </span>
  ) : undefined;

  return (
    <AdminWorkspace
      modules={modules}
      breadcrumb={crumbs}
      toolbarActions={
        <div className="dash-header-actions">
          {classicHref && (
            <Link to={toClassicHref(classicHref)} className="dash-classic-link">
              <ArrowLeft size={13} /> {classicLabel}
            </Link>
          )}
          {onExitV2 && (
            <button className="dash-classic-link" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} onClick={onExitV2}>
              <ArrowLeft size={13} /> {classicLabel}
            </button>
          )}
          <div className="dash-toolbar-user"><V2UserMenu /></div>
        </div>
      }
    >
      {children}
    </AdminWorkspace>
  );
};

interface V2PageHeaderProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: React.ReactNode;
  /** Azioni a destra (bottoni pill); il toggle tema è incluso automaticamente */
  actions?: React.ReactNode;
}

export const V2PageHeader: React.FC<V2PageHeaderProps> = ({ icon, title, subtitle, actions }) => (
  <div className="a2-wrap" style={{ paddingTop: 16, display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 260 }}>
      <span style={{ width: 46, height: 46, borderRadius: 14, background: 'var(--a2-green-500)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
        {icon}
      </span>
      <div>
        <h1 style={{ margin: 0, font: "700 24px/1.15 'Sora', sans-serif", letterSpacing: '-.02em', color: 'var(--a2-tx)' }}>{title}</h1>
        {subtitle && <p style={{ margin: '3px 0 0', font: "500 13px 'DM Sans', sans-serif", color: 'var(--a2-mut)' }}>{subtitle}</p>}
      </div>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <V2ThemeToggle />
      {actions}
    </div>
  </div>
);

/**
 * Incapsula una pagina admin CLASSICA nel guscio V2 (redesign 2026).
 * La pagina resta l'unica fonte (zero drift): viene renderizzata
 * identica dentro V2Shell; il suo breadcrumb interno è nascosto via
 * CSS (.adm2 nav[aria-label="Breadcrumb"]). Usare per i moduli non
 * ancora ridisegnati nativamente.
 */
export const V2ClassicWrap: React.FC<{ classicHref: string; label: string; children: React.ReactNode }> =
  ({ classicHref, label, children }) => (
    <V2Shell
      classicHref={classicHref}
      breadcrumb={[{ label: 'Dashboard', to: '/admin' }, { label }]}
    >
      {children}
    </V2Shell>
  );

/**
 * Wrapper per la "modalità V2 persistente": quando `active` è true avvolge
 * il contenuto (le route admin classiche di 2°/3° livello) nel guscio V2,
 * con bottone "Esci dalla nuova interfaccia". Quando false, passa i figli
 * invariati (nessun impatto sulle pagine pubbliche o sulla modalità classica).
 */
export const V2ModeWrap: React.FC<{ active: boolean; onExitV2: () => void; label: string; children: React.ReactNode }> =
  ({ active, onExitV2, label, children }) => {
    if (!active) return <>{children}</>;
    return (
      <V2Shell
        onExitV2={onExitV2}
        classicLabel="Esci dalla nuova interfaccia"
        breadcrumb={[{ label: 'Dashboard', to: '/admin' }, { label }]}
      >
        {children}
      </V2Shell>
    );
  };

export default V2Shell;
